import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import {
  SESSION_STORAGE_KEY,
  BILLING_LAYOUT_KEY,
  BILL_TYPE_KEY,
  PANEL_VIEW_KEY,
} from "../Constant";
import { DiscountMode } from "../Enums";

export type BillingLayout = "standard" | "compact";
export type BillType = "pdf_invoice" | "thermal_80mm" | "a4_invoice";
export type PanelViewMode = "tab" | "simple";

export interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  batch_no: string;
  qty: number;
  rawProduct: any;
  cost_price?: number;
  is_tempory_item?: boolean;
  item_name?: string | null;
  description?: string | null;
  is_print?: boolean;
  printed_qty?: number;
  discount_amount?: number;
  discount_input?: number;
  discount_mode?: DiscountMode;
  sold_price?: number;
}

interface CartContextType {
  cart: CartItem[];
  cartTotal: number;
  cartItemsCount: number;
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  addToCart: (
    product: any,
    batch: any,
    price: number,
    qty: number,
    rawProduct: any,
    cost_price?: number,
    description?: string | null,
    is_print?: boolean,
    printed_qty?: number,
    discount_amount?: number,
    sold_price?: number,
    discount_input?: number,
    discount_mode?: DiscountMode,
  ) => void;
  addEditingDraftObj: (obj: any) => void;
  removeFromCart: (id: number, batchNo: string) => void;
  clearCart: () => void;
  customer: { id: number; name: string; credit_limit?: number } | null;
  setCustomer: (customer: { id: number; name: string; credit_limit?: number } | null) => void;
  table: { id: string; name: string } | null;
  setTable: (table: { id: string; name: string } | null) => void;
  isWholeSale: boolean;
  setIsWholeSale: (val: boolean) => void;
  billingLayout: BillingLayout;
  setBillingLayout: (val: BillingLayout) => void;
  billType: BillType;
  setBillType: (val: BillType) => void;
  panelViewMode: PanelViewMode;
  setPanelViewMode: (val: PanelViewMode) => void;
  editingOrderId: string | null;
  setEditingOrderId: (id: string | null) => void;
  editingDraftId: string | null;
  setEditingDraftId: (id: string | null) => void;
  editingQuotationId: string | null;
  setEditingQuotationId: (id: string | null) => void;
  editingQuotationName: string | null;
  setEditingQuotationName: (name: string | null) => void;
  proceedingQuotationId: string | null;
  setProceedingQuotationId: (id: string | null) => void;
  quotationTempCustomer: { name: string; phone?: string; email?: string; address?: string } | null;
  setQuotationTempCustomer: (cust: { name: string; phone?: string; email?: string; address?: string } | null) => void;
  exchangeDiscount: number;
  setExchangeDiscount: (val: number) => void;
  originalDraftItems: any[];
  setOriginalDraftItems: (items: any[]) => void;
  repairJobId: string | null;
  setRepairJobId: (id: string | null) => void;
  repairJobAdvance: number;
  setRepairJobAdvance: (val: number) => void;
  repairJobPaymentMethodId: string | null;
  setRepairJobPaymentMethodId: (id: string | null) => void;
  onlineOrderId: string | null;
  setOnlineOrderId: (id: string | null) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  // ─── Cart ──────────────────────────────────────────────────────────────────
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // ─── Customer ──────────────────────────────────────────────────────────────
  const [customer, setCustomerState] = useState<{
    id: number;
    name: string;
    credit_limit?: number;
  } | null>(() => {
    try {
      const stored = sessionStorage.getItem("pos_customer");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // ─── Table ─────────────────────────────────────────────────────────────────
  const [table, setTableState] = useState<{ id: string; name: string } | null>(
    () => {
      try {
        const stored = sessionStorage.getItem("pos_table");
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    },
  );

  // ─── Wholesale ─────────────────────────────────────────────────────────────
  const [isWholeSale, setIsWholeSaleState] = useState<boolean>(() => {
    try {
      const stored = sessionStorage.getItem("pos_is_wholesale");
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // ─── Editing Order ──────────────────────────────────────────────────────────
  const [editingOrderId, setEditingOrderIdState] = useState<string | null>(
    () => {
      try {
        return sessionStorage.getItem("pos_editing_order_id");
      } catch {
        return null;
      }
    },
  );

  // ─── Editing Draft ──────────────────────────────────────────────────────────
  const [editingDraftId, setEditingDraftIdState] = useState<string | null>(
    () => {
      try {
        return sessionStorage.getItem("pos_editing_draft_id");
      } catch {
        return null;
      }
    },
  );

  const [editingQuotationId, setEditingQuotationIdState] = useState<string | null>(
    () => {
      try {
        return sessionStorage.getItem("pos_editing_quotation_id");
      } catch {
        return null;
      }
    },
  );

  const [editingQuotationName, setEditingQuotationNameState] = useState<string | null>(
    () => {
      try {
        return sessionStorage.getItem("pos_editing_quotation_name");
      } catch {
        return null;
      }
    },
  );

  const [proceedingQuotationId, setProceedingQuotationIdState] = useState<string | null>(
    () => {
      try {
        return sessionStorage.getItem("pos_proceeding_quotation_id");
      } catch {
        return null;
      }
    },
  );

  const [quotationTempCustomer, setQuotationTempCustomerState] = useState<any>(
    () => {
      try {
        const stored = sessionStorage.getItem("pos_quotation_temp_customer");
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    },
  );

  // ─── Exchange Discount (Returned Item Exchange Offset) ──────────────────────
  const [exchangeDiscount, setExchangeDiscountState] = useState<number>(() => {
    try {
      const stored = sessionStorage.getItem("pos_exchange_discount");
      return stored ? parseFloat(stored) : 0;
    } catch {
      return 0;
    }
  });

  // ─── POS Preferences (localStorage) ───────────────────────────────────────
  const [billingLayout, setBillingLayoutState] = useState<BillingLayout>(
    () =>
      (localStorage.getItem(BILLING_LAYOUT_KEY) as BillingLayout) || "standard",
  );

  const [billType, setBillTypeState] = useState<BillType>(
    () => (localStorage.getItem(BILL_TYPE_KEY) as BillType) || "pdf_invoice",
  );

  const [panelViewMode, setPanelViewModeState] = useState<PanelViewMode>(
    () => (localStorage.getItem(PANEL_VIEW_KEY) as PanelViewMode) || "tab",
  );

  // ─── Repair Job POS Context State ──────────────────────────────────────────
  const [repairJobId, setRepairJobIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("pos_repair_job_id");
    } catch {
      return null;
    }
  });

  const [repairJobAdvance, setRepairJobAdvanceState] = useState<number>(() => {
    try {
      const stored = sessionStorage.getItem("pos_repair_job_advance");
      return stored ? parseFloat(stored) : 0;
    } catch {
      return 0;
    }
  });

  const [repairJobPaymentMethodId, setRepairJobPaymentMethodIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("pos_repair_job_pm_id");
    } catch {
      return null;
    }
  });

  // ─── Online Order POS Context State ────────────────────────────────────────
  const [onlineOrderId, setOnlineOrderIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("pos_online_order_id");
    } catch {
      return null;
    }
  });

  // ─── Setters ───────────────────────────────────────────────────────────────
  const setCustomer = useCallback(
    (cust: { id: number; name: string; credit_limit?: number } | null) => {      
      setCustomerState(cust);
      if (cust) {
        sessionStorage.setItem("pos_customer", JSON.stringify(cust));
      } else {
        sessionStorage.removeItem("pos_customer");
      }
    },
    [],
  );

  const setTable = useCallback((tbl: { id: string; name: string } | null) => {
    setTableState(tbl);
    if (tbl) {
      sessionStorage.setItem("pos_table", JSON.stringify(tbl));
    } else {
      sessionStorage.removeItem("pos_table");
    }
  }, []);

  const setIsWholeSale = useCallback((val: boolean) => {
    setIsWholeSaleState(val);
    sessionStorage.setItem("pos_is_wholesale", JSON.stringify(val));
  }, []);

  const setBillingLayout = useCallback((val: BillingLayout) => {
    setBillingLayoutState(val);
    localStorage.setItem(BILLING_LAYOUT_KEY, val);
  }, []);

  const setBillType = useCallback((val: BillType) => {
    setBillTypeState(val);
    localStorage.setItem(BILL_TYPE_KEY, val);
  }, []);

  const setPanelViewMode = useCallback((val: PanelViewMode) => {
    setPanelViewModeState(val);
    localStorage.setItem(PANEL_VIEW_KEY, val);
  }, []);

  const setEditingOrderId = useCallback((id: string | null) => {
    setEditingOrderIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_editing_order_id", id);
    } else {
      sessionStorage.removeItem("pos_editing_order_id");
    }
  }, []);

  const setEditingDraftId = useCallback((id: string | null) => {
    setEditingDraftIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_editing_draft_id", id);
    } else {
      sessionStorage.removeItem("pos_editing_draft_id");
    }
  }, []);

  const setEditingQuotationId = useCallback((id: string | null) => {
    setEditingQuotationIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_editing_quotation_id", id);
    } else {
      sessionStorage.removeItem("pos_editing_quotation_id");
    }
  }, []);

  const setEditingQuotationName = useCallback((name: string | null) => {
    setEditingQuotationNameState(name);
    if (name !== null) {
      sessionStorage.setItem("pos_editing_quotation_name", name);
    } else {
      sessionStorage.removeItem("pos_editing_quotation_name");
    }
  }, []);

  const setProceedingQuotationId = useCallback((id: string | null) => {
    setProceedingQuotationIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_proceeding_quotation_id", id);
    } else {
      sessionStorage.removeItem("pos_proceeding_quotation_id");
    }
  }, []);

  const setQuotationTempCustomer = useCallback((cust: any) => {
    setQuotationTempCustomerState(cust);
    if (cust !== null) {
      sessionStorage.setItem("pos_quotation_temp_customer", JSON.stringify(cust));
    } else {
      sessionStorage.removeItem("pos_quotation_temp_customer");
    }
  }, []);

  const setExchangeDiscount = useCallback((val: number) => {
    setExchangeDiscountState(val);
    sessionStorage.setItem("pos_exchange_discount", String(val));
  }, []);

  const setRepairJobId = useCallback((id: string | null) => {
    setRepairJobIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_repair_job_id", id);
    } else {
      sessionStorage.removeItem("pos_repair_job_id");
    }
  }, []);

  const setRepairJobAdvance = useCallback((val: number) => {
    setRepairJobAdvanceState(val);
    sessionStorage.setItem("pos_repair_job_advance", String(val));
  }, []);

  const setRepairJobPaymentMethodId = useCallback((id: string | null) => {
    setRepairJobPaymentMethodIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_repair_job_pm_id", id);
    } else {
      sessionStorage.removeItem("pos_repair_job_pm_id");
    }
  }, []);

  const setOnlineOrderId = useCallback((id: string | null) => {
    setOnlineOrderIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_online_order_id", id);
    } else {
      sessionStorage.removeItem("pos_online_order_id");
    }
  }, []);

  const [originalDraftItems, setOriginalDraftItems] = useState<any[]>(() => {
    try {
      const stored = sessionStorage.getItem("pos_original_draft_items");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify(
          cart.map((item) => ({
            id: item.id,
            name: item.name,
            price: item.price,
            image: item.image,
            batch_no: item.batch_no,
            qty: item.qty,
            rawProduct: item.rawProduct,
            cost_price: item.cost_price,
            is_tempory_item: item.is_tempory_item,
            item_name: item.item_name,
            description: item.description,
            is_print: item.is_print,
            printed_qty: item.printed_qty,
            discount_amount: item.discount_amount,
            discount_input: item.discount_input,
            discount_mode: item.discount_mode,
            sold_price: item.sold_price,
          })),
        ),
      );
    } catch (e) {
      console.error("Error saving cart to sessionStorage", e);
    }
  }, [cart]);

  // ─── Cart actions ──────────────────────────────────────────────────────────
  const addToCart = useCallback(
    (
      product: any,
      batch: any,
      price: number,
      qty: number,
      rawProduct: any,
      cost_price?: number,
      description?: string | null,
      is_print?: boolean,
      printed_qty?: number,
      discount_amount?: number,
      sold_price?: number,
      discount_input?: number,
      discount_mode?: DiscountMode,
    ) => {
      setCart((prevCart) => {
        const existing = prevCart.find(
          (item) => item.id === product.id && item.batch_no === batch.batch_no,
        );
        let updated: CartItem[];

        if (existing) {
          updated = prevCart.map((item) =>
            item.id === product.id && item.batch_no === batch.batch_no
              ? {
                  ...item,
                  qty: item.qty + qty,
                  price,
                  cost_price: cost_price ?? item.cost_price,
                  description:
                    description !== undefined ? description : item.description,
                  is_print: is_print !== undefined ? is_print : item.is_print,
                  printed_qty:
                    printed_qty !== undefined ? printed_qty : item.printed_qty,
                  discount_amount:
                    discount_amount !== undefined
                      ? discount_amount
                      : item.discount_amount,
                  sold_price:
                    sold_price !== undefined
                      ? sold_price
                      : item.sold_price,
                  discount_input:
                    discount_input !== undefined
                      ? discount_input
                      : item.discount_input,
                  discount_mode:
                    discount_mode !== undefined
                      ? discount_mode
                      : item.discount_mode,
                }
              : item,
          );
        } else {
          updated = [
            ...prevCart,
            {
              id: product.id,
              name: product.title,
              price: price,
              image: product.image,
              batch_no: batch.batch_no,
              qty: qty,
              rawProduct: rawProduct,
              cost_price: cost_price,
              is_tempory_item: rawProduct?.is_tempory_item || false,
              item_name: null,
              description: description || null,
              is_print: is_print || false,
              printed_qty: printed_qty || 0,
              discount_amount: discount_amount ?? 0,
              discount_input: discount_input ?? 0,
                        discount_mode: discount_mode ?? DiscountMode.AMOUNT,
              sold_price: sold_price ?? 0,
            },
          ];
        }
        return updated;
      });
    },
    [],
  );

  const addEditingDraftObj = (obj: any) => {
    try {
      if (obj === null) {
        sessionStorage.removeItem("pos_editing_draft_obj");
        return;
      }
      sessionStorage.setItem("pos_editing_draft_obj", JSON.stringify(obj));
    } catch (e) {
      console.error("Error saving cart to sessionStorage", e);
    }
  };

  const removeFromCart = useCallback((id: number, batchNo: string) => {
    setCart((prevCart) =>
      prevCart.filter((item) => !(item.id === id && item.batch_no === batchNo)),
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setCustomer(null);
    setTable(null);
    setEditingOrderId(null);
    setEditingDraftId(null);
    setEditingQuotationId(null);
    setEditingQuotationName(null);
    setProceedingQuotationId(null);
    setQuotationTempCustomer(null);
    setExchangeDiscount(0);
    setRepairJobId(null);
    setRepairJobAdvance(0);
    setRepairJobPaymentMethodId(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem("pos_editing_draft_id");
    sessionStorage.removeItem("pos_original_draft_items");
    sessionStorage.removeItem("pos_editing_quotation_id");
    sessionStorage.removeItem("pos_editing_quotation_name");
    sessionStorage.removeItem("pos_proceeding_quotation_id");
    sessionStorage.removeItem("pos_quotation_temp_customer");
    sessionStorage.removeItem("pos_repair_job_id");
    sessionStorage.removeItem("pos_repair_job_advance");
    sessionStorage.removeItem("pos_repair_job_pm_id");
  }, [
    setCustomer,
    setTable,
    setEditingOrderId,
    setEditingDraftId,
    setEditingQuotationId,
    setEditingQuotationName,
    setProceedingQuotationId,
    setQuotationTempCustomer,
    setExchangeDiscount,
    setRepairJobId,
    setRepairJobAdvance,
    setRepairJobPaymentMethodId,
  ]);

  const cartTotal = useMemo(() => {
    return cart.reduce(
      (acc, item) => acc + item.price * item.qty - (item.discount_amount ?? 0),
      0,
    );
  }, [cart]);

  const cartItemsCount = useMemo(() => {
    return cart.length;
  }, [cart]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartTotal,
        cartItemsCount,
        customer,
        setCustomer,
        table,
        setTable,
        addToCart,
        addEditingDraftObj,
        removeFromCart,
        clearCart,
        setCart,
        isWholeSale,
        setIsWholeSale,
        billingLayout,
        setBillingLayout,
        billType,
        setBillType,
        panelViewMode,
        setPanelViewMode,
        editingOrderId,
        setEditingOrderId,
        editingDraftId,
        setEditingDraftId,
        editingQuotationId,
        setEditingQuotationId,
        editingQuotationName,
        setEditingQuotationName,
        proceedingQuotationId,
        setProceedingQuotationId,
        quotationTempCustomer,
        setQuotationTempCustomer,
        exchangeDiscount,
        setExchangeDiscount,
        originalDraftItems,
        setOriginalDraftItems,
        repairJobId,
        setRepairJobId,
        repairJobAdvance,
        setRepairJobAdvance,
        repairJobPaymentMethodId,
        setRepairJobPaymentMethodId,
        onlineOrderId,
        setOnlineOrderId,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
